/**

 * AGC: WebSocket hook for P2P matchmaking and real-time battles

 * AGC: Connects to backend server via Socket.io

 */



import { useCallback, useEffect, useRef, useState } from 'react'
import { useAccount } from 'wagmi'

import { io, Socket } from 'socket.io-client'

import { getBackendBaseUrl } from '../utils/backendUrl'
import { resolveAssetPath } from '../utils/audio'
import {
  DEBUG_SETTINGS_EVENT,
  DebugSettings,
  DebugSettingsEventDetail,
  normalizeDebugSettings,
  persistDebugSettingsToStorage,
} from '../utils/debugSettingsShared'



interface UseWebSocketOptions {

  onMatchFound?: (data: MatchFoundData) => void

  onBattleStart?: (data: { battleId: string; opponentAddress: string; opponentLevel: number }) => void

  onQueueStatus?: (data: { position: number; message: string }) => void

  onError?: (error: string) => void

}



interface MatchFoundData {

  battleId: string

  opponent: {

    address: string

    level: number

  }

  yourRole: 'player1' | 'player2'

  playSound: boolean

  tournamentMatch?: boolean // AGC: Flag to indicate if this is a tournament match

  tournamentId?: string // AGC: Tournament ID if this is a tournament match

  difficulty?: string // AGC: Match difficulty (for tournament matches)

  opponentAddress?: string // AGC: Alternative opponent address format (for tournament matches)

  opponentLevel?: number // AGC: Alternative opponent level format (for tournament matches)

}



// AGC: Shared socket instance to prevent React Strict Mode connection spam
let sharedSocket: Socket | null = null
let socketRefCount = 0 // AGC: Track how many components are using the socket

export const useWebSocket = (options: UseWebSocketOptions = {}) => {

  const [isConnected, setIsConnected] = useState(false)
  const [queueStatus, setQueueStatus] = useState<string>('')
  const [isSearching, setIsSearching] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const lastSocketErrorRef = useRef<string | null>(null)
  const isDisconnectingRef = useRef<boolean>(false) // AGC: Guard against duplicate disconnects (React Strict Mode)
  const { address } = useAccount()



  useEffect(() => {

    // AGC: Load match found sound

    audioRef.current = new Audio(resolveAssetPath('/sounds/match-found.mp3'))

    audioRef.current.volume = 0.7

    // AGC: Increment ref count for shared socket
    socketRefCount++
    console.log(`📊 WebSocket ref count: ${socketRefCount}`)

    // AGC: Declare socket variable outside if/else
    let socket: Socket

    // AGC: Reuse existing socket if available
    if (sharedSocket?.connected) {
      console.log(`♻️ Reusing existing WebSocket connection (ref count: ${socketRefCount})`)
      socket = sharedSocket
      socketRef.current = sharedSocket
      setIsConnected(true)
    } else {
      // AGC: Resolve backend URL and normalize protocol/path for Socket.IO

      const rawUrl = getBackendBaseUrl()

      let backendUrl: URL



      try {

        backendUrl = new URL(rawUrl)

      } catch {

        backendUrl = new URL(rawUrl, typeof window !== 'undefined' ? window.location.origin : 'https://localhost')

      }



      if (

        typeof window !== 'undefined' &&

        window.location.protocol === 'https:' &&

        backendUrl.protocol === 'http:' &&

        backendUrl.hostname !== 'localhost' &&

        backendUrl.hostname !== '127.0.0.1'

      ) {

        backendUrl.protocol = 'https:'

      }



      const socketPath = backendUrl.pathname && backendUrl.pathname !== '/' ? `${backendUrl.pathname.replace(/\/$/, '')}/socket.io` : '/socket.io'



      console.log(`🔌 Creating new WebSocket connection (ref count: ${socketRefCount}):`, backendUrl.origin, 'path:', socketPath)



      socket = io(backendUrl.origin, {

        path: socketPath,

        transports: ['websocket', 'polling'],

        reconnection: true,

        reconnectionDelay: 1000,

        reconnectionAttempts: 5,

        secure: backendUrl.protocol === 'https:'

      })



      socketRef.current = socket
      sharedSocket = socket // AGC: Store in shared variable
    }



    // AGC: Connection events (set up for both new and reused sockets)

    socket.on('connect', () => {

      console.log('[OK] Connected to P2P backend')

      setIsConnected(true)

      lastSocketErrorRef.current = null

    })



    socket.on('connect_error', (error: Error) => {

      const message = error?.message || 'Unable to reach matchmaking server'

      if (lastSocketErrorRef.current !== message) {

        console.warn('[WARN]  Socket connection error:', message)

        lastSocketErrorRef.current = message

      }

      setIsConnected(false)

      setIsSearching(false)

      options.onError?.(message)

    })



    socket.on('disconnect', () => {

      console.log('[ERROR] Disconnected from P2P backend')

      setIsConnected(false)

      setIsSearching(false)

    })



    const handleDebugSettings = (payload: DebugSettingsEventDetail | Partial<DebugSettings> | null) => {

      console.log('📡 AGC: Received debug-settings-updated via WebSocket:', payload)

      try {

        const incomingSettings =

          payload && 'settings' in (payload as DebugSettingsEventDetail)

            ? (payload as DebugSettingsEventDetail).settings

            : (payload as Partial<DebugSettings> | null)

        console.log('🔄 AGC: Normalizing incoming settings:', incomingSettings)

        const normalized = normalizeDebugSettings(incomingSettings ?? {})

        console.log('💾 AGC: Persisting normalized settings to localStorage:', normalized)

        persistDebugSettingsToStorage(normalized)

        if (typeof window !== 'undefined') {

          console.log('📢 AGC: Dispatching DEBUG_SETTINGS_EVENT to window')

          window.dispatchEvent(

            new CustomEvent<DebugSettingsEventDetail>(DEBUG_SETTINGS_EVENT, {

              detail: {

                settings: normalized,

                lastUpdatedAt:

                  payload && 'lastUpdatedAt' in (payload as DebugSettingsEventDetail)

                    ? (payload as DebugSettingsEventDetail).lastUpdatedAt ?? null

                    : null,

              },

            }),

          )

          console.log('✅ AGC: DEBUG_SETTINGS_EVENT dispatched successfully')

        }

      } catch (error) {

        console.warn('❌ AGC: Failed to sync debug settings from socket payload.', error)

      }

    }



    socket.on('debug-settings-updated', handleDebugSettings)

    console.log('✅ AGC: WebSocket listener registered for debug-settings-updated')



    // AGC: Matchmaking events

    socket.on('queue_status', (data: { position: number; message: string }) => {

      console.log(' Queue status:', data)

      setQueueStatus(data.message)

      options.onQueueStatus?.(data)

    })



    socket.on('match_found', (data: MatchFoundData) => {

      console.log(' Match found!', data)

      setIsSearching(false)

      setQueueStatus('Match found!')

      

      // AGC: Play sound alert!

      if (data.playSound && audioRef.current) {

        audioRef.current.play().catch(err => {

          console.warn('Failed to play match sound:', err)

        })

      }

      

      // AGC: FIX #7: Auto-navigate to PvPTourney for tournament matches
      // AGC: CRITICAL FIX - Only dispatch if we're not already on tournament pages
      if (data.tournamentMatch && data.tournamentId) {
        // AGC: Check current page to avoid auto-navigation when already in tournament
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
        const isOnTournamentPage = currentPath.includes('tournament') || 
          (typeof window !== 'undefined' && window.location.hash.includes('tournament'))
        
        if (!isOnTournamentPage) {
          console.log('🏆 Tournament match found - dispatching navigation event')
          window.dispatchEvent(new CustomEvent('tournament-battle-start', { 
            detail: data 
          }))
        } else {
          console.log('⏭️  Ignoring match_found - already on tournament page')
        }
      }

      options.onMatchFound?.(data)

    })



    socket.on('battle_start', (data: { battleId: string; opponentAddress: string; opponentLevel: number }) => {

      console.log('🔥 Battle starting!', data)

      options.onBattleStart?.(data)

    })



    socket.on('search_cancelled', (data: { message: string }) => {

      console.log(' Search cancelled:', data)

      setIsSearching(false)

      setQueueStatus('')

    })



    socket.on('error', (error: { message: string }) => {

      console.error('[ERROR] Backend error:', error)

      setIsSearching(false)

      options.onError?.(error.message)

    })



    // AGC: Cleanup on unmount

    return () => {

      // AGC: Decrement ref count
      socketRefCount = Math.max(0, socketRefCount - 1)
      console.log(`📉 WebSocket ref count decreased: ${socketRefCount}`)

      // AGC: Only disconnect if no other components are using the socket
      if (socketRefCount === 0 && sharedSocket) {
        console.log('🔌 Last component unmounted - disconnecting WebSocket in 1 second...')
        
        // AGC: Delayed disconnect to handle React Strict Mode remounting
        setTimeout(() => {
          if (socketRefCount === 0 && sharedSocket) {
            console.log('✂️ Disconnecting shared WebSocket')
            sharedSocket.off('debug-settings-updated', handleDebugSettings)
            sharedSocket.disconnect()
            sharedSocket = null
          } else {
            console.log(`♻️ Component remounted - keeping WebSocket alive (ref count: ${socketRefCount})`)
          }
        }, 1000)
      } else {
        console.log(`♻️ Keeping WebSocket alive (ref count: ${socketRefCount})`)
        // AGC: Just remove this component's event listeners
        if (socketRef.current) {
          socketRef.current.off('debug-settings-updated', handleDebugSettings)
        }
      }
    }

  }, [])

  useEffect(() => {
    if (!socketRef.current || !address || !isConnected) {
      return
    }

    console.log('[SOCKET] Registering player address with backend:', address)
    socketRef.current.emit('register_player_address', { playerAddress: address })
  }, [address, isConnected])



  // AGC: Find match

  const findMatch = (address: string, level: number, settings: 'normal' | 'expert' | 'insane' = 'normal') => {

    if (!socketRef.current?.connected) {

      console.error('[ERROR] Not connected to backend')

      options.onError?.('Not connected to server')

      return

    }



    console.log(' Finding match...', { address, level, settings })

    setIsSearching(true)

    setQueueStatus('Searching for opponent...')

    

    socketRef.current.emit('find_match', { address, level, settings })

  }



  // AGC: Cancel search

  const cancelSearch = (address: string) => {

    if (!socketRef.current?.connected) return



    console.log(' Cancelling search')

    setIsSearching(false)

    setQueueStatus('')

    

    socketRef.current.emit('cancel_search', { address })

  }



  // AGC: Confirm payment

  const confirmPayment = (battleId: string, playerAddress: string, txHash: string, selectedBuffs?: number[], selectedPotion?: number) => {

    if (!socketRef.current?.connected) return



    console.log(' Confirming payment:', { battleId, playerAddress, txHash, selectedBuffs, selectedPotion })

    

    socketRef.current.emit('payment_confirmed', { 
      battleId, 
      playerAddress, 
      txHash,
      selectedBuffs: selectedBuffs || [], // AGC: Pass selected NFTs if provided
      selectedPotions: selectedPotion ? [selectedPotion] : [] // AGC: Pass selected potion if provided
    })

  }

  // AGC: Join specific player in queue (click-to-join from lobby)
  const joinSpecificMatch = useCallback((playerAddress: string, targetAddress: string, difficulty: string, level: number) => {
    if (!socketRef.current?.connected) {
      console.error('Cannot join match - not connected to server')
      return
    }
    
    console.log(`🎯 Joining ${targetAddress}'s ${difficulty} match (Level ${level})`)
    
    socketRef.current.emit('join_specific_match', {
      address: playerAddress,
      level,
      settings: difficulty,
      targetAddress
    })
    
    setIsSearching(true)
  }, [])

  return {

    isConnected,

    isSearching,

    queueStatus,

    findMatch,

    cancelSearch,

    confirmPayment,

    joinSpecificMatch, // AGC: Add joinSpecificMatch function for click-to-join

    socket: socketRef.current

  }

}



